import Child from '../core/Child';
import LinkList from './LinkList';
import MetaActionList from './MetaActionList';
import MetaPageList from './MetaPageList';
import MetaQueryList from './MetaQueryList';
import MetaFieldList from './MetaFieldList';
import MetaField from './MetaField';

class Meta extends Child {
  constructor(args = {}) {
    super(args);

    const {
      data = {},
      metaOverrideSet,
    } = args;

    const {
      actions = [],
      template = [],
      queries = [],
      links = {},
      page = {},
      exclusionDependencies = {},
      type,
    } = data;

    this.actionList = new MetaActionList({ data: actions, parent: this });
    this.linkList = new LinkList({ data: links, parent: this });
    this.pageList = new MetaPageList({ data: page, parent: this });
    this.queryList = new MetaQueryList({ data: queries, parent: this, metaOverrideSet });
    this.fieldList = new MetaFieldList({ data: template, parent: this, metaOverrideSet });
    this.exclusionDependenciesList = exclusionDependencies;

    this._type = type;

    this.fieldList.orderBy('ordinal');
  }

  action(key) {
    if (key === undefined) {
      throw new Error('Usage: Key is required.');
    }

    const action = this.actionList.item(key);

    if (!action) return null;

    return action.attr('name');
  }

  canGet() {
    return !!this.action('GET');
  }

  canPost() {
    return !!this.action('POST');
  }

  canPut() {
    return !!this.action('PUT');
  }

  canSubmit() {
    return this.canPut() || this.canPost();
  }

  canDelete() {
    return !!this.action('DELETE');
  }

  actions() {
    return this.actionList;
  }

  export() {
    return {
      actions: this.actionList.list({ ignoreFilters: true }).map(v => v.export()).map(v => v.name),
      links: this.linkList.export(),
      page: this.pageList.export(),
      queries: this.queryList.list({ ignoreFilters: true }).map(v => v.export()),
      template: this.fieldList.list({ ignoreFilters: true }).map(v => v.export()),
      exclusionDependencies: this.exclusionDependenciesList,
      type: this._type,
    };
  }

  link(key) {
    if (key === undefined) {
      throw new Error('Usage: Key is required.');
    }

    const link = this.linkList.item(key);

    if (!link) return null;

    return link;
  }

  merge(other) {
    if (!this.shouldMerge(other)) {
      return;
    }

    if (other) {
      this.actionList.merge(other.actionList);
      this.linkList.merge(other.linkList);
      this.pageList.merge(other.pageList);
      this.queryList.merge(other.queryList);
      this.fieldList.merge(other.fieldList);
    }

    return this;
  }

  page(key) {
    if (key === undefined) {
      throw new Error('Usage: Key is required.');
    }

    const page = this.pageList.item(key);

    if (!page) return null;

    return page.attr('value');
  }

  queries() {
    return this.queryList;
  }

  query(key) {
    if (key === undefined) {
      throw new Error('Usage: Key is required.');
    }

    return this.queryList.item(key);
  }

  template() {
    return this.fieldList;
  }

  exclusionDependencies() {
    return this.exclusionDependenciesList;
  }

  field(key) {
    if (key === undefined) {
      throw new Error('Usage: Key is required.');
    }

    const field = this.fieldList.item(key);

    if (!field) return new MetaField({ parent: this, isEmpty: true });

    return this.fieldList.item(key);
  }

  type() {
    return this._type || null;
  }
}

export default Meta;
